/**
 * Le due forme di credenziali (02-ACCESSO §1), e il passo 1 dei quattro.
 *
 * Non c'è un gettone di sessione: non c'è `access_token`, non c'è `refresh_token`, non
 * c'è `Authorization: Bearer`. Le credenziali conservate **sono** la sessione e viaggiano
 * in query string a ogni richiesta (02-ACCESSO §4). Non c'è niente da rinnovare, quindi
 * non c'è niente che possa scadere a metà di un film.
 */

export interface TokenCredentials {
  readonly kind: 'token';
  readonly host: string;
  /**
   * Il nome con cui l'installazione si presenta: «STAGE», «Romus».
   *
   * Arriva nella busta dell'accoppiamento, insieme a host e gettone, perché quello è l'unico istante
   * in cui l'apparecchio scopre a chi appartiene. È **solo di comodo** — serve a non mostrare un
   * indirizzo nelle impostazioni — e non decide niente: non va usato per confronti né per scegliere
   * comportamenti, altrimenti un operatore che si rinomina cambierebbe quel che l'app fa.
   *
   * `null` quando l'istanza non lo manda: è il caso «l'altra parte è più vecchia», non un errore.
   * Chi disegna mostra l'host, come prima.
   */
  readonly serviceName: string | null;
  /** `line.playlist_token`: 128 caratteri, rigenerabile dal pannello. */
  readonly token: string;
}

export interface StandardCredentials {
  readonly kind: 'standard';
  readonly host: string;
  readonly username: string;
  readonly password: string;
}

export type Credentials = TokenCredentials | StandardCredentials;

/**
 * Le ultime quattro cifre, e nient'altro.
 *
 * Il player non scrive mai le credenziali nei propri registri, né nelle schermate di
 * diagnosi, né nelle segnalazioni di errore (00-PERIMETRO §5, 02-ACCESSO §4.3).
 */
export function redactSecret(secret: string): string {
  const tail = secret.slice(-4);
  return tail.length === 0 ? '••••' : `••••${tail}`;
}

/**
 * Il token lungo si mostra troncato **a sinistra**: la coda è la parte che distingue,
 * l'inizio no (03-SCHERMATE-TV §3).
 */
export function tailOf(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return `…${value.slice(value.length - maxLength + 1)}`;
}

export type HostProblem =
  | 'empty'
  | 'invalid_scheme'
  | 'invalid_host'
  | 'invalid_port'
  | 'has_path';

export type HostCheck =
  | {
      readonly ok: true;
      readonly normalized: string;
      /**
       * `true` quando lo schema non c'era e l'abbiamo **messo noi**.
       *
       * Serve alla schermata per dirlo prima dell'invio. `https` è il valore giusto per una
       * piattaforma in produzione, ma chi prova in sviluppo scrive `localhost:3106` e finisce su
       * `https://localhost:3106`: la richiesta non arriva, e il rifiuto che ne esce somiglia in
       * tutto a «credenziali sbagliate». Il 28-09-2026 ci ho perso un giro completo di diagnosi,
       * convinto che il difetto fosse nella forma della credenziale.
       *
       * Non si cambia il valore assunto — indovinare `http` per un indirizzo pubblico sarebbe molto
       * peggio, perché manderebbe una password in chiaro. Si **dice**, e chi non lo voleva scrive
       * lo schema.
       */
      readonly schemeAssumed: boolean;
    }
  | { readonly ok: false; readonly problem: HostProblem };

const SCHEME = /^([a-z][a-z0-9+.-]*):\/\//i;
const HOSTNAME = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i;
const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

/**
 * Passo 1 dei quattro: «è un host scrivibile». **Locale**, nessuna rete.
 *
 * Due cose che il prototipo sbagliava e che qui sono requisiti:
 *
 *  - **`http` è ammesso.** Ci sono gateway dietro Apache su porte alte e gateway
 *    raggiunti per indirizzo IP. Rifiutare `http` significherebbe rifiutare
 *    installazioni che esistono.
 *  - **La porta va conservata.** `http://93.41.2.7:8080` è un host valido e completo;
 *    troncarlo alla parte prima dei due punti è l'errore che rende l'app inutilizzabile
 *    su metà delle installazioni di collaudo.
 *
 * Quando lo schema manca si assume `https`, e questo non è un ripiego silenzioso: è la
 * scelta più protettiva delle due, ed è visibile nel campo perché la funzione torna
 * l'host normalizzato che l'interfaccia rimostra.
 */
export function checkHost(raw: string): HostCheck {
  const input = raw.trim();
  if (input.length === 0) return { ok: false, problem: 'empty' };

  let scheme = 'https';
  let schemeAssumed = true;
  let rest = input;
  const schemeMatch = SCHEME.exec(input);
  if (schemeMatch) {
    scheme = (schemeMatch[1] ?? '').toLowerCase();
    if (scheme !== 'http' && scheme !== 'https') return { ok: false, problem: 'invalid_scheme' };
    schemeAssumed = false;
    rest = input.slice(schemeMatch[0].length);
  }

  rest = rest.replace(/\/+$/, '');
  if (rest.includes('/')) return { ok: false, problem: 'has_path' };
  if (rest.length === 0) return { ok: false, problem: 'invalid_host' };

  let hostname = rest;
  let port: string | null = null;
  const colon = rest.lastIndexOf(':');
  if (colon > 0) {
    hostname = rest.slice(0, colon);
    port = rest.slice(colon + 1);
    if (!/^\d{1,5}$/.test(port)) return { ok: false, problem: 'invalid_port' };
    const portNumber = Number(port);
    if (portNumber < 1 || portNumber > 65535) return { ok: false, problem: 'invalid_port' };
  }

  const ipv4 = IPV4.exec(hostname);
  if (ipv4) {
    for (let i = 1; i <= 4; i += 1) {
      const octet = Number(ipv4[i]);
      if (!Number.isInteger(octet) || octet > 255) return { ok: false, problem: 'invalid_host' };
    }
  } else if (!HOSTNAME.test(hostname)) {
    return { ok: false, problem: 'invalid_host' };
  }

  return {
    ok: true,
    normalized: `${scheme}://${hostname.toLowerCase()}${port ? `:${port}` : ''}`,
    schemeAssumed,
  };
}

/**
 * I parametri di autenticazione da mettere in query string. Stessa forma per ogni rotta,
 * `PATCH` compresa (12-BACKEND-E-GATEWAY §2).
 */
export function credentialsQuery(credentials: Credentials): ReadonlyArray<readonly [string, string]> {
  if (credentials.kind === 'token') return [['token', credentials.token]];
  return [
    ['username', credentials.username],
    ['password', credentials.password],
  ];
}

/**
 * Cosa entra nel campo quando si incolla.
 *
 * Sta nel dominio e non nella schermata perché è una **regola**, non un dettaglio di disegno, e
 * perché è provabile senza un browser. La schermata decide *quando* incollare; questo decide *cosa*.
 *
 * Toglie **ogni** spazio bianco, non solo quelli ai bordi: un token copiato da una tabella, da un
 * messaggio o da un'email arriva spesso con un ritorno a capo in mezzo o in fondo, e un solo
 * carattere invisibile trasforma una credenziale giusta in `auth.invalid_token` — un rifiuto che
 * accusa il cliente di un errore che non ha fatto. Né host né token né utente contengono spazi per
 * costruzione, quindi non si perde niente di legittimo.
 *
 * Restituisce `null` quando non resta niente: chi incolla una riga vuota non intendeva svuotare il
 * campo, e cancellargli quello che aveva scritto sarebbe la risposta sbagliata a un gesto a vuoto.
 */
export function cleanPastedCredential(text: string): string | null {
  const cleaned = text.replace(/\s+/g, '');
  return cleaned.length > 0 ? cleaned : null;
}
